import {RemoteConstantsModel} from "../models/remoteConstants.model";
import {AttributeManifest} from "../models/attributeManifest.model";
import {ChoicePrompt} from "../app.context";
import {CommandBase} from "../commands/commandBase";
import {RemoveImageCommand} from "../commands/removeImage.command";
import {classifyImageKeys} from "./imageClassification";
import React from "react";

interface OrphanCleanupDeps {
    state: RemoteConstantsModel;
    getLocalizationValue(id: string): string;
    attributeManifest: AttributeManifest | undefined;
    choose(prompt: ChoicePrompt): Promise<string | undefined>;
    executeCommand(command: CommandBase): void;
    goBack(): void;
}

// Call AFTER executing a command that removed a reference to `key` (pack-slot
// removal, slot/icon re-pick, upload-over). If the image just lost its last
// usage, offers:
//   Archive — remove the entry, file recoverable from the Archive tab
//   Delete  — remove the entry permanently (no archive record)
//   third button, by `cancelBehavior`:
//     'undo' (default) — Cancel: revert the reference-removing command itself.
//       Assumes exactly ONE command was executed since the removal (goBack
//       undoes one history step) — callers must not batch commands before this.
//     'keep' — Keep: leave the image in place as an unused orphan. For flows
//       that executed several commands (e.g. upload-over), where a single
//       undo step can't cleanly revert.
export async function offerOrphanImageCleanup(
    key: string,
    deps: OrphanCleanupDeps,
    cancelBehavior: 'undo' | 'keep' | 'auto-archive' = 'undo',
): Promise<void> {
    if (!key || !(key in (deps.state.images ?? {}))) return;

    // The state object is mutated in place by commands, so a fresh
    // classification here reflects the just-executed removal.
    const info = classifyImageKeys(deps.state, deps.getLocalizationValue, deps.attributeManifest).get(key);
    if (!info || info.usedBy.length > 0) return;

    // 'auto-archive': a retired entity-image key (bare images[<id>]) lost its
    // last reference because its pack top-left was repointed. The standalone
    // "entity image" is a retired concept the user shouldn't have to manage, so
    // archive it silently (recoverable from the Archive tab) — no prompt.
    if (cancelBehavior === 'auto-archive') {
        deps.executeCommand(new RemoveImageCommand(key));
        return;
    }

    const result = await deps.choose({
        title: 'Image No Longer Used',
        body: React.createElement('p', null,
            'Image ', React.createElement('strong', null, info.label),
            ` (${key}) is now unused. Archive it (recoverable from the Archive tab), ` +
            'delete it permanently, or ' +
            (cancelBehavior === 'undo' ? 'cancel the removal?' : 'keep it unused?')),
        options: [
            {text: 'Archive', value: 'archive'},
            {text: 'Delete permanently', value: 'delete', danger: true},
            cancelBehavior === 'undo'
                ? {text: 'Cancel', value: 'cancel'}
                : {text: 'Keep', value: 'keep'},
        ],
    });

    if (result === 'archive') {
        deps.executeCommand(new RemoveImageCommand(key));
    } else if (result === 'delete') {
        deps.executeCommand(new RemoveImageCommand(key, false));
    } else if (cancelBehavior === 'undo') {
        // 'cancel' or dismissed: revert the original reference removal.
        deps.goBack();
    }
    // 'keep' (or dismissed in keep-mode): nothing to do — the orphan stays.
}
